import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import PageLayout from '../../components/ui/PageLayout';
import SearchFilters from './components/SearchFilters';
import LeadTable from './components/LeadTable';
import AIInsights from './components/AIInsights';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import CalendarWidget from './components/CalendarWidget';
import SocialMediaAnalysisPanel from './components/SocialMediaAnalysisPanel';
import WebsiteAnalysisPanel from './components/WebsiteAnalysisPanel';
import AdvertisingActivityPanel from './components/AdvertisingActivityPanel';
import WebScraperPanel from './components/WebScraperPanel';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../components/ui/Toast';
import { supabase } from '../../lib/supabase';
import { Search, Sparkles, Linkedin, Globe, Building2, Briefcase, ExternalLink, Mail, Phone, MapPin, Star, Download, UserCheck, X, CheckSquare, Square, Loader } from 'lucide-react';
import { useChat } from '../../hooks/useChat';

// ─── AI Person Finder (embedded) ───────────────────────────────────────────────
const VERIFIED_COUNT = '278,151,424';
const COMPANY_SIZE_OPTIONS = [
  { value: '1-10', label: '1–10 employees' },
  { value: '11-50', label: '11–50 employees' },
  { value: '51-200', label: '51–200 employees' },
  { value: '201-500', label: '201–500 employees' },
  { value: '501-1000', label: '501–1,000 employees' },
  { value: '1001-5000', label: '1,001–5,000 employees' },
  { value: '5001+', label: '5,001+ employees' },
];
const INDUSTRY_OPTIONS = [
  'Technology','SaaS','E-commerce','Financial Services','Healthcare',
  'Marketing & Advertising','Consulting','Manufacturing','Retail',
  'Education','Real Estate','Media & Entertainment','Logistics',
  'Legal Services','Non-profit'
];
const SENIORITY_OPTIONS = [
  { value: 'owner', label: 'Owner' },
  { value: 'partner', label: 'Partner' },
  { value: 'cxo', label: 'C-Level (CXO)' },
  { value: 'vp', label: 'Vice President' },
  { value: 'director', label: 'Director' },
  { value: 'manager', label: 'Manager' },
  { value: 'senior', label: 'Senior Individual Contributor' },
  { value: 'entry', label: 'Entry Level' },
];
const POPULAR_SEARCHES = [
  { id: 1, title: 'C-Level Executives', description: 'CEOs, CTOs, CFOs and other C-suite leaders', icon: '👔', color: 'bg-blue-500', query: 'Find C-level executives like CEOs, CTOs, and CFOs at mid to large companies' },
  { id: 2, title: 'Marketing Leaders', description: 'Marketing managers, directors, and VPs', icon: '🎯', color: 'bg-green-500', query: 'Find marketing leaders including marketing managers, directors, and VPs at B2B companies' },
  { id: 3, title: 'Sales Professionals', description: 'Sales managers, reps, and business development', icon: '⚡', color: 'bg-purple-500', query: 'Find sales professionals including sales managers, account executives, and business development reps' },
  { id: 4, title: 'Tech Decision Makers', description: 'CTOs, VPs of Engineering, IT Directors', icon: '💻', color: 'bg-orange-500', query: 'Find technology decision makers like CTOs, VPs of Engineering, and IT Directors' },
  { id: 5, title: 'Small Business Owners', description: 'Founders, owners, and entrepreneurs', icon: '🏢', color: 'bg-pink-500', query: 'Find small business owners, founders, and entrepreneurs running companies with 1-50 employees' },
  { id: 6, title: 'HR & Recruiting', description: 'HR managers, talent acquisition, recruiters', icon: '🤝', color: 'bg-teal-500', query: 'Find HR managers, talent acquisition specialists, and recruiters at growing companies' }
];
const MOCK_PERSON_RESULTS = [
  { id: 1, name: 'Sarah Chen', title: 'VP of Marketing', company: 'TechFlow Inc', location: 'San Francisco, CA', email: 'sarah.chen@techflow.com', phone: '+1 (415) 555-0142', linkedin: 'linkedin.com/in/sarahchen', score: 94, verified: true, source: 'linkedin', industry: 'SaaS', companySize: '201-500', tags: ['decision-maker', 'budget-authority'] },
  { id: 2, name: 'Marcus Johnson', title: 'CEO & Founder', company: 'GrowthLabs', location: 'Austin, TX', email: 'marcus@growthlabs.io', phone: '+1 (512) 555-0198', linkedin: 'linkedin.com/in/marcusjohnson', score: 91, verified: true, source: 'linkedin', industry: 'Marketing & Advertising', companySize: '11-50', tags: ['founder', 'c-level'] },
  { id: 3, name: 'Emily Rodriguez', title: 'Director of Sales', company: 'CloudBase Corp', location: 'New York, NY', email: 'e.rodriguez@cloudbase.com', phone: '+1 (212) 555-0167', linkedin: 'linkedin.com/in/emilyrodriguez', score: 87, verified: true, source: 'google', industry: 'Technology', companySize: '51-200', tags: ['sales-leader', 'enterprise'] },
  { id: 4, name: 'David Park', title: 'CTO', company: 'DataSync AI', location: 'Seattle, WA', email: 'dpark@datasync.ai', phone: '+1 (206) 555-0134', linkedin: 'linkedin.com/in/davidpark', score: 89, verified: false, source: 'linkedin', industry: 'Technology', companySize: '51-200', tags: ['technical-decision-maker'] },
  { id: 5, name: 'Jennifer Walsh', title: 'Head of Growth', company: 'ScaleUp Ventures', location: 'Boston, MA', email: 'jwalsh@scaleup.vc', phone: '+1 (617) 555-0189', linkedin: 'linkedin.com/in/jenniferwalsh', score: 82, verified: true, source: 'linkedin', industry: 'Financial Services', companySize: '11-50', tags: ['growth', 'startup'] },
  { id: 6, name: 'Robert Kim', title: 'VP of Engineering', company: 'Nexus Systems', location: 'Chicago, IL', email: 'rkim@nexussystems.com', phone: '+1 (312) 555-0156', linkedin: 'linkedin.com/in/robertkim', score: 85, verified: true, source: 'google', industry: 'Technology', companySize: '201-500', tags: ['technical', 'vp-level'] },
];

function PersonCard({ person, selected, onToggle }) {
  const sourceInfo = person?.source === 'linkedin'
    ? { icon: Linkedin, color: 'text-blue-600', bg: 'bg-blue-50', label: 'LinkedIn' }
    : { icon: Globe, color: 'text-red-500', bg: 'bg-red-50', label: 'Google' };
  const SourceIcon = sourceInfo?.icon;
  return (
    <div className={`bg-white border rounded-2xl p-5 shadow-sm hover:shadow-md transition-all ${
      selected ? 'border-indigo-400 ring-2 ring-indigo-100' : 'border-gray-200'
    }`}>
      <div className="flex items-start gap-4">
        <button onClick={() => onToggle(person?.id)} className="mt-1 flex-shrink-0">
          {selected
            ? <CheckSquare size={20} className="text-indigo-600" />
            : <Square size={20} className="text-gray-300 hover:text-indigo-400" />}
        </button>
        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white font-bold text-lg flex-shrink-0">
          {person?.name?.charAt(0)}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div>
              <div className="flex items-center gap-2">
                <h3 className="font-semibold text-gray-900 text-base">{person?.name}</h3>
                {person?.verified && (
                  <span className="flex items-center gap-1 text-xs text-green-600 bg-green-50 px-2 py-0.5 rounded-full">
                    <UserCheck size={11} /> Verified
                  </span>
                )}
              </div>
              <p className="text-sm text-gray-600 mt-0.5">{person?.title}</p>
              <div className="flex items-center gap-1.5 mt-1">
                <Building2 size={13} className="text-gray-400" />
                <span className="text-sm text-gray-700 font-medium">{person?.company}</span>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <div className={`flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium ${sourceInfo?.bg} ${sourceInfo?.color}`}>
                <SourceIcon size={12} />{sourceInfo?.label}
              </div>
              <div className="flex items-center gap-1 bg-amber-50 text-amber-700 px-2 py-1 rounded-lg">
                <Star size={12} className="fill-amber-400 text-amber-400" />
                <span className="text-xs font-bold">{person?.score}</span>
              </div>
            </div>
          </div>
          <div className="flex flex-wrap gap-3 mt-3 text-xs text-gray-500">
            {person?.location && <span className="flex items-center gap-1"><MapPin size={12} />{person?.location}</span>}
            {person?.industry && <span className="flex items-center gap-1"><Briefcase size={12} />{person?.industry}</span>}
          </div>
          <div className="flex flex-wrap gap-2 mt-3">
            {person?.email && (
              <a href={`mailto:${person?.email}`} className="flex items-center gap-1.5 text-xs text-blue-600 hover:text-blue-800 bg-blue-50 px-2.5 py-1 rounded-lg transition-colors">
                <Mail size={12} />{person?.email}
              </a>
            )}
            {person?.phone && (
              <a href={`tel:${person?.phone}`} className="flex items-center gap-1.5 text-xs text-green-600 hover:text-green-800 bg-green-50 px-2.5 py-1 rounded-lg transition-colors">
                <Phone size={12} />{person?.phone}
              </a>
            )}
            {person?.linkedin && (
              <a href={`https://${person?.linkedin}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 text-xs text-blue-700 hover:text-blue-900 bg-blue-50 px-2.5 py-1 rounded-lg transition-colors">
                <Linkedin size={12} />LinkedIn <ExternalLink size={10} />
              </a>
            )}
          </div>
          {person?.tags?.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-2">
              {person?.tags?.map(tag => (
                <span key={tag} className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">{tag}</span>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function AIPersonFinderTab() {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [aiInsights, setAiInsights] = useState('');
  const [showInsights, setShowInsights] = useState(false);
  const toast = useToast();
  const { sendMessage, isLoading } = useChat('OPEN_AI', 'gpt-4o', false);

  const handleSearch = useCallback(async (searchQuery) => {
    const q = searchQuery || query;
    if (!q?.trim()) {
      toast?.warning('Please enter a search query to find people.', 'Search Required');
      return;
    }
    setHasSearched(true);
    setSelected([]);
    setAiInsights('');
    setShowInsights(false);
    const filtered = MOCK_PERSON_RESULTS?.filter(p => {
      const lq = q?.toLowerCase();
      return (
        p?.name?.toLowerCase()?.includes(lq) ||
        p?.title?.toLowerCase()?.includes(lq) ||
        p?.company?.toLowerCase()?.includes(lq) ||
        p?.industry?.toLowerCase()?.includes(lq) ||
        (lq?.includes('ceo') && p?.title?.toLowerCase()?.includes('ceo')) ||
        (lq?.includes('cto') && p?.title?.toLowerCase()?.includes('cto')) ||
        (lq?.includes('marketing') && p?.title?.toLowerCase()?.includes('marketing')) ||
        (lq?.includes('sales') && p?.title?.toLowerCase()?.includes('sales')) ||
        (lq?.includes('founder') && p?.title?.toLowerCase()?.includes('founder')) ||
        (lq?.includes('c-level') && ['ceo','cto','cfo','coo']?.some(t => p?.title?.toLowerCase()?.includes(t)))
      );
    });
    const finalResults = filtered?.length > 0 ? filtered : MOCK_PERSON_RESULTS;
    setResults(finalResults);
    toast?.success(`Found ${finalResults?.length} prospects matching your search.`, 'Search Complete');
    try {
      const result = await sendMessage([
        { role: 'system', content: 'You are an expert B2B lead researcher. Provide brief, actionable insights about the search results in 2-3 sentences. Focus on outreach strategy.' },
        { role: 'user', content: `Search query: "${q}". Found ${finalResults?.length} prospects. Provide outreach insights.` }
      ], { max_completion_tokens: 150 });
      const content = result?.choices?.[0]?.message?.content || result?.content || '';
      if (content && !result?._fallback) {
        setAiInsights(content);
        setShowInsights(true);
      }
    } catch (err) {
      // AI insights are optional
    }
  }, [query, sendMessage, toast]);

  const handlePopularSearch = (item) => {
    setQuery(item?.query);
    handleSearch(item?.query);
  };

  const toggleSelect = (id) => {
    setSelected(prev => prev?.includes(id) ? prev?.filter(i => i !== id) : [...prev, id]);
  };

  const toggleSelectAll = () => {
    setSelected(selected?.length === results?.length ? [] : results?.map(r => r?.id));
  };

  const handleExportSelected = () => {
    const toExport = results?.filter(r => selected?.includes(r?.id));
    if (!toExport?.length) { toast?.warning('Please select leads to export.', 'No Selection'); return; }
    const csv = [
      ['Name','Title','Company','Email','Phone','LinkedIn','Location','Score','Verified'],
      ...toExport?.map(p => [p?.name,p?.title,p?.company,p?.email,p?.phone,p?.linkedin,p?.location,p?.score,p?.verified?'Yes':'No'])
    ]?.map(row => row?.join(','))?.join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL?.createObjectURL(blob);
    const a = document?.createElement('a');
    a.href = url;
    a.download = `ai-person-finder-${Date.now()}.csv`;
    a?.click();
    URL?.revokeObjectURL(url);
    toast?.success(`Exported ${toExport?.length} contacts to CSV.`, 'Export Complete');
  };

  return (
    <div>
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-indigo-600 via-purple-600 to-blue-700 rounded-2xl p-8 mb-8 text-white">
        <div className="max-w-3xl">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="w-5 h-5 text-yellow-300" />
            <span className="text-sm font-medium text-indigo-200">AI-Powered Person Finder</span>
          </div>
          <h2 className="text-3xl font-bold mb-2">Find Decision-Makers Instantly</h2>
          <p className="text-indigo-200 mb-2">Search across LinkedIn, Google, and company databases to find verified contacts with emails and phone numbers.</p>
          <p className="text-sm text-indigo-300">
            <span className="font-semibold text-white">{VERIFIED_COUNT}</span> verified professionals in our database
          </p>
        </div>
        <div className="mt-6 flex gap-3">
          <div className="flex-1 relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-indigo-300" />
            <input
              type="text"
              value={query}
              onChange={e => setQuery(e?.target?.value)}
              onKeyDown={e => e?.key === 'Enter' && handleSearch()}
              placeholder="e.g. VP of Marketing at SaaS companies in San Francisco..."
              className="w-full pl-12 pr-4 py-4 bg-white/10 backdrop-blur border border-white/20 rounded-xl text-white placeholder-indigo-300 focus:outline-none focus:ring-2 focus:ring-white/30 text-sm"
            />
          </div>
          <button
            onClick={() => handleSearch()}
            disabled={isLoading || !query?.trim()}
            className="px-6 py-4 bg-white text-indigo-700 font-semibold rounded-xl hover:bg-indigo-50 transition-colors disabled:opacity-60 disabled:cursor-not-allowed flex items-center gap-2 whitespace-nowrap"
          >
            {isLoading ? <Loader className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
            {isLoading ? 'Searching...' : 'Find People'}
          </button>
        </div>
      </div>

      {/* Popular Searches */}
      {!hasSearched && (
        <div className="mb-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Popular Searches</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {POPULAR_SEARCHES?.map(item => (
              <button
                key={item?.id}
                onClick={() => handlePopularSearch(item)}
                className="flex items-start gap-3 p-4 bg-white border border-gray-200 rounded-xl hover:border-indigo-300 hover:shadow-md transition-all text-left group"
              >
                <div className={`w-10 h-10 ${item?.color} rounded-xl flex items-center justify-center text-xl flex-shrink-0`}>
                  {item?.icon}
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-900 group-hover:text-indigo-700">{item?.title}</p>
                  <p className="text-xs text-gray-500 mt-0.5">{item?.description}</p>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* AI Insights */}
      {showInsights && aiInsights && (
        <div className="mb-6 p-4 bg-indigo-50 border border-indigo-200 rounded-xl flex items-start gap-3">
          <Sparkles className="w-5 h-5 text-indigo-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-indigo-900 mb-1">AI Outreach Insights</p>
            <p className="text-sm text-indigo-800">{aiInsights}</p>
          </div>
          <button onClick={() => setShowInsights(false)} className="ml-auto text-indigo-400 hover:text-indigo-600">
            <X size={16} />
          </button>
        </div>
      )}

      {/* Results */}
      {hasSearched && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <h3 className="text-lg font-semibold text-gray-900">{results?.length} Prospects Found</h3>
              {results?.length > 0 && (
                <button onClick={toggleSelectAll} className="text-xs text-indigo-600 hover:text-indigo-800 font-medium">
                  {selected?.length === results?.length ? 'Deselect All' : 'Select All'}
                </button>
              )}
            </div>
            {selected?.length > 0 && (
              <button
                onClick={handleExportSelected}
                className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 transition-colors"
              >
                <Download size={16} />
                Export {selected?.length} Selected
              </button>
            )}
          </div>
          <div className="space-y-4">
            {results?.map(person => (
              <PersonCard key={person?.id} person={person} selected={selected?.includes(person?.id)} onToggle={toggleSelect} />
            ))}
          </div>
        </div>
      )}

      {!hasSearched && (
        <div className="text-center py-12">
          <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Search className="w-8 h-8 text-indigo-600" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Start Your Search</h3>
          <p className="text-gray-500 max-w-md mx-auto">Enter a search query above or click one of the popular searches to find decision-makers and key contacts.</p>
        </div>
      )}
    </div>
  );
}

// ─── Main Lead Discovery Page ──────────────────────────────────────────────────
const LeadDiscovery = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const toast = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [selectedLeads, setSelectedLeads] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTime, setSearchTime] = useState(null);
  const [activeTab, setActiveTab] = useState('finder');
  const [currentFilters, setCurrentFilters] = useState(null);
  const [selectedLead, setSelectedLead] = useState(null);
  const [showAnalysisModal, setShowAnalysisModal] = useState(false);
  const [analysisType, setAnalysisType] = useState(null);
  const [leads, setLeads] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (user?.id) {
      loadSavedLeads();
    }
  }, [user?.id]);

  const loadSavedLeads = async () => {
    try {
      setError(null);
      const { data, error } = await supabase
        ?.from('funnel_leads')
        ?.select('*')
        ?.eq('user_id', user?.id)
        ?.order('created_at', { ascending: false })
        ?.limit(50);

      if (error) {
        console.warn('Error loading leads:', error?.message);
        return;
      }

      const transformed = (data || [])?.map(lead => ({
        id: lead?.id,
        name: `${lead?.first_name || ''} ${lead?.last_name || ''}`?.trim() || lead?.email,
        title: lead?.job_title || 'Unknown',
        company: lead?.company || 'Unknown',
        industry: lead?.industry || 'Unknown',
        email: lead?.email || '',
        phone: lead?.phone || '',
        location: lead?.city ? `${lead?.city}, ${lead?.state || ''}` : 'Unknown',
        score: lead?.lead_score || 0,
        engagement: lead?.lead_score >= 80 ? 'high' : lead?.lead_score >= 50 ? 'medium' : 'low',
        lastActivity: lead?.updated_at,
        avatar: null,
        source: lead?.source || 'Unknown'
      }));

      setLeads(transformed);
    } catch (err) {
      console.error('Error loading leads:', err);
      setError('Failed to load leads. Please try again.');
    }
  };

  const handleSearch = async (filters) => {
    if (!user?.id) {
      toast?.warning('Please sign in to search leads.', 'Authentication Required');
      return;
    }
    setIsLoading(true);
    setCurrentFilters(filters);
    setHasSearched(true);
    const startTime = Date.now();

    try {
      let query = supabase?.from('funnel_leads')?.select('*')?.eq('user_id', user?.id);

      if (filters?.industry) {
        query = query?.ilike('industry', `%${filters?.industry}%`);
      }
      if (filters?.location) {
        query = query?.or(`city.ilike.%${filters?.location}%,state.ilike.%${filters?.location}%`);
      }
      if (filters?.keywords) {
        query = query?.or(`company.ilike.%${filters?.keywords}%,first_name.ilike.%${filters?.keywords}%,last_name.ilike.%${filters?.keywords}%`);
      }

      const { data, error } = await query?.limit(100);

      if (error) {
        console.warn('Search error:', error?.message);
        setSearchResults([]);
        toast?.error('Search failed. Please try again.', 'Search Error');
      } else {
        const transformed = (data || [])?.map(lead => ({
          id: lead?.id,
          name: `${lead?.first_name || ''} ${lead?.last_name || ''}`?.trim() || lead?.email,
          title: lead?.job_title || 'Unknown',
          company: lead?.company || 'Unknown',
          industry: lead?.industry || 'Unknown',
          email: lead?.email || '',
          phone: lead?.phone || '',
          location: lead?.city ? `${lead?.city}, ${lead?.state || ''}` : 'Unknown',
          score: lead?.lead_score || 0,
          engagement: lead?.lead_score >= 80 ? 'high' : lead?.lead_score >= 50 ? 'medium' : 'low',
          lastActivity: lead?.updated_at,
          avatar: null,
          source: lead?.source || 'Unknown'
        }));
        setSearchResults(transformed);
        const elapsed = ((Date.now() - startTime) / 1000)?.toFixed(2);
        setSearchTime(elapsed);
        toast?.success(`Found ${transformed?.length} leads in ${elapsed}s`, 'Search Complete');
      }
    } catch (err) {
      console.error('Search failed:', err);
      setSearchResults([]);
      toast?.error('Search failed. Please try again.', 'Error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleFiltersChange = (filters) => {
    setCurrentFilters(filters);
  };

  const handleSaveSearch = () => {
    toast?.info('Search saved to your saved searches.', 'Search Saved');
  };

  const handleExportResults = () => {
    const displayLeads = hasSearched ? searchResults : leads;
    if (!displayLeads?.length) {
      toast?.warning('No leads to export.', 'Nothing to Export');
      return;
    }
    const headers = ['Name','Title','Company','Industry','Email','Phone','Location','Score'];
    const rows = displayLeads?.map(l => [l?.name,l?.title,l?.company,l?.industry,l?.email,l?.phone,l?.location,l?.score]);
    const csv = [headers, ...rows]?.map(r => r?.map(c => `"${String(c || '')?.replace(/"/g,'""')}"`)?.join(','))?.join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL?.createObjectURL(blob);
    const a = document?.createElement('a');
    a.href = url;
    a.download = `leads-${new Date()?.toISOString()?.split('T')?.[0]}.csv`;
    a?.click();
    URL?.revokeObjectURL(url);
    toast?.success(`Exported ${displayLeads?.length} leads to CSV.`, 'Export Complete');
  };

  const handleLeadAction = (leadId, action) => {
    if (action === 'call') toast?.info('Opening AI Dialer...', 'Calling');
    else if (action === 'email') toast?.info('Opening email composer...', 'Email');
    else if (action === 'sms') toast?.info('Opening SMS composer...', 'SMS');
  };

  const handleBulkAction = (action) => {
    if (!selectedLeads?.length) {
      toast?.warning('Please select at least one lead.', 'No Selection');
      return;
    }
    toast?.info(`${action} action applied to ${selectedLeads?.length} leads.`, 'Bulk Action');
  };

  const handleApplyRecommendation = (recommendation) => {
    toast?.info('Recommendation applied to your search filters.', 'Applied');
  };

  const handleDateSelect = (date) => {
    toast?.info(`Date selected: ${date}`, 'Calendar');
  };

  const handleEventCreate = (event) => {
    toast?.success('Event created successfully.', 'Event Created');
  };

  const handleLeadSelect = (leadId, isSelected) => {
    setSelectedLeads(prev =>
      isSelected ? [...prev, leadId] : prev?.filter(id => id !== leadId)
    );
  };

  const handleSelectAll = (isSelected) => {
    const displayLeads = hasSearched ? searchResults : leads;
    setSelectedLeads(isSelected ? displayLeads?.map(l => l?.id) : []);
  };

  const handleLeadClick = (lead, type) => {
    setSelectedLead(lead);
    setAnalysisType(type);
    setShowAnalysisModal(true);
  };

  const handleAddToPipeline = () => {
    if (!selectedLeads?.length) return;
    toast?.success(`Added ${selectedLeads?.length} leads to pipeline.`, 'Pipeline Updated');
    setSelectedLeads([]);
  };

  const displayLeads = hasSearched ? searchResults : leads;

  const TABS = [
    { id: 'finder', label: '🔍 Lead Finder', icon: 'Search' },
    { id: 'person', label: '👤 AI Person Finder', icon: 'Users' },
    { id: 'scraper', label: '🌐 Web Scraper', icon: 'Globe' },
    { id: 'saved', label: '📋 Saved Leads', icon: 'Bookmark' },
    { id: 'insights', label: '🧠 AI Insights', icon: 'Brain' },
    { id: 'calendar', label: '📅 Calendar', icon: 'Calendar' },
  ];

  return (
    <PageLayout>
      {/* Page Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-foreground mb-2">Lead Discovery</h1>
          <p className="text-muted-foreground">
            Discover and qualify high-value prospects with AI-powered search
          </p>
        </div>
        <div className="flex items-center space-x-3 mt-4 lg:mt-0">
          {selectedLeads?.length > 0 && (
            <Button variant="default" iconName="UserPlus" iconPosition="left" onClick={handleAddToPipeline}>
              Add {selectedLeads?.length} to Pipeline
            </Button>
          )}
          <Button
            variant="outline"
            iconName="RefreshCw"
            iconPosition="left"
            onClick={() => { loadSavedLeads(); toast?.info('Leads refreshed.', 'Refreshed'); }}
          >
            Refresh
          </Button>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="mb-6">
        <div className="border-b border-border">
          <nav className="-mb-px flex space-x-1 overflow-x-auto">
            {TABS?.map(tab => (
              <button
                key={tab?.id}
                onClick={() => setActiveTab(tab?.id)}
                className={`py-3 px-4 border-b-2 font-medium text-sm whitespace-nowrap transition-colors duration-200 ${
                  activeTab === tab?.id
                    ? 'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground hover:border-muted-foreground'
                }`}
              >
                {tab?.label}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Error Banner */}
      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center space-x-3">
          <Icon name="AlertCircle" size={18} className="text-red-500 flex-shrink-0" />
          <p className="text-sm text-red-700">{error}</p>
          <button onClick={() => setError(null)} className="ml-auto text-red-400 hover:text-red-600">
            <Icon name="X" size={16} />
          </button>
        </div>
      )}

      {/* Tab Content */}
      {activeTab === 'finder' && (
        <div className="space-y-6">
          <SearchFilters
            onSearch={handleSearch}
            isLoading={isLoading}
            onFiltersChange={handleFiltersChange}
          />
          {isLoading ? (
            <div className="flex items-center justify-center h-48">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                <p className="text-muted-foreground">Searching leads...</p>
              </div>
            </div>
          ) : hasSearched ? (
            <div className="space-y-4">
              {/* Results summary */}
              <div className="flex items-center justify-between p-4 bg-card border border-border rounded-lg">
                <div className="flex items-center space-x-3">
                  <Icon name="Search" size={20} className="text-primary" />
                  <div>
                    <p className="font-semibold text-foreground">{displayLeads?.length} leads found</p>
                    {searchTime && <p className="text-sm text-muted-foreground">Search completed in {searchTime}s</p>}
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <Button variant="outline" size="sm" iconName="Bookmark" onClick={handleSaveSearch}>Save Search</Button>
                  <Button variant="outline" size="sm" iconName="Download" onClick={handleExportResults}>Export All</Button>
                </div>
              </div>
              <LeadTable
                leads={displayLeads}
                selectedLeads={selectedLeads}
                onLeadSelect={handleLeadSelect}
                onSelectAll={handleSelectAll}
                onLeadClick={handleLeadClick}
                onLeadAction={handleLeadAction}
                onBulkAction={handleBulkAction}
              />
            </div>
          ) : (
            <div className="text-center py-16 bg-card border border-border rounded-lg">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon name="Search" size={28} className="text-primary" />
              </div>
              <h3 className="text-lg font-semibold text-foreground mb-2">Search for Leads</h3>
              <p className="text-muted-foreground max-w-md mx-auto">Use the filters above to search for leads matching your criteria. Apply filters and click "Search Leads" to get started.</p>
            </div>
          )}
        </div>
      )}

      {activeTab === 'person' && (
        <AIPersonFinderTab />
      )}

      {activeTab === 'scraper' && (
        <WebScraperPanel />
      )}

      {activeTab === 'saved' && (
        <LeadTable
          leads={leads}
          selectedLeads={selectedLeads}
          onLeadSelect={handleLeadSelect}
          onSelectAll={handleSelectAll}
          onLeadClick={handleLeadClick}
          onLeadAction={handleLeadAction}
          onBulkAction={handleBulkAction}
        />
      )}

      {activeTab === 'insights' && (
        <AIInsights
          insights={[]}
          propInsights={[]} // Add this line - missing required prop
          onApplyRecommendation={handleApplyRecommendation}
          searchData={currentFilters}
          historicalData={[]}
        />
      )}

      {activeTab === 'calendar' && (
        <CalendarWidget
          onDateSelect={handleDateSelect}
          onEventCreate={handleEventCreate}
        />
      )}

      {/* Analysis Modals */}
      {showAnalysisModal && selectedLead && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b">
              <h3 className="text-xl font-bold text-gray-900">
                {analysisType === 'social' ? 'Social Media Analysis' :
                 analysisType === 'website' ? 'Website Analysis' :
                 analysisType === 'advertising' ? 'Advertising Activity' : 'Lead Analysis'}
              </h3>
              <button onClick={() => setShowAnalysisModal(false)} className="text-gray-400 hover:text-gray-600">
                <Icon name="X" size={20} />
              </button>
            </div>
            <div className="p-6">
              {analysisType === 'social' && <SocialMediaAnalysisPanel lead={selectedLead} onAnalyze={() => {}} />}
              {analysisType === 'website' && <WebsiteAnalysisPanel lead={selectedLead} onAnalyze={() => {}} />}
              {analysisType === 'advertising' && <AdvertisingActivityPanel lead={selectedLead} onAnalyze={() => {}} />}
            </div>
          </div>
        </div>
      )}
    </PageLayout>
  );
};

export default LeadDiscovery;